import { currentLocale } from "../lib/i18n";

const STORAGE_KEY = "ub-shopify-entry-context";

export interface ShopifyEntryContext {
  host: string;
  locale: string;
}

interface StoredShopifyEntryContext extends ShopifyEntryContext {
  shop: string;
}

function nonEmpty(value: string | null): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

/** Shopify が query / App Bridge で返す canonical myshopify domain だけを許可する。 */
function canonicalShop(value: string | null | undefined): string | null {
  const shop = nonEmpty(value ?? null)?.toLowerCase() ?? null;
  if (!shop) return null;
  return /^(?=.{1,63}\.myshopify\.com$)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.myshopify\.com$/.test(
    shop,
  )
    ? shop
    : null;
}

function readStored(): StoredShopifyEntryContext | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;

    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      return null;
    }

    const record = value as Record<string, unknown>;
    if (
      typeof record.shop !== "string" ||
      typeof record.host !== "string" ||
      typeof record.locale !== "string"
    ) {
      return null;
    }

    const shop = canonicalShop(record.shop);
    const host = nonEmpty(record.host);
    const locale = nonEmpty(record.locale);
    if (!shop || !host || !locale) return null;

    return { shop, host, locale };
  } catch {
    return null;
  }
}

/** 初回 iframe URL の Shopify 文脈を、SPA 内遷移で失わないよう保持する。 */
export function captureShopifyEntryContext(search: string): void {
  const params = new URLSearchParams(search);
  const host = nonEmpty(params.get("host"));
  const shop = canonicalShop(params.get("shop"));

  try {
    // host だけを保存すると、同じタブで別ストアへ入ったときに古い Admin handle
    // へ戻し得る。entry URL から得た canonical shop と必ず対で保存する。
    if (host && shop) {
      const context: StoredShopifyEntryContext = {
        shop,
        host,
        locale: nonEmpty(params.get("locale")) ?? currentLocale(),
      };
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    }
  } catch {
    // Storage が使えない環境では現在 URL だけを使う。host を推測して送らない。
  }
}

/** probe POST に必要な文脈。host を復元できなければ fail-closed。 */
export function referralProbeContext(): ShopifyEntryContext | null {
  const params = new URLSearchParams(window.location.search);
  const currentShop =
    canonicalShop(params.get("shop")) ??
    canonicalShop(window.shopify?.config?.shop);
  const stored = readStored();
  const currentHost = nonEmpty(params.get("host"));
  if (currentHost) {
    return {
      host: currentHost,
      locale:
        nonEmpty(params.get("locale")) ??
        (currentShop && currentShop === stored?.shop ? stored.locale : null) ??
        currentLocale(),
    };
  }

  if (!currentShop || !stored || currentShop !== stored.shop) return null;

  const locale = nonEmpty(params.get("locale")) ?? stored.locale;
  return { host: stored.host, locale };
}
